// loyalty-phone.js

const LOYALTY_SELECTORS = {
  NEXT_BUTTON: '[data-loyalty-next-button]',
  REDIRECT_URL: '[data-loyalty-redirect]',
  ERROR_MESSAGE: '[data-loyalty-error]',
  QUESTION_INDICATOR: '[data-loyalty-question-indicator]',
};

class LoyaltyBirthday extends HTMLElement {
  constructor() {
    super();
    this.viewModel = null;
    this.state = {
      selectedDay: null,
      selectedMonth: null,
      isSubmitting: false,
      errors: {},
    };
    this._boundHandlers = [];
  }

  connectedCallback() {
    this.viewModel = this.getViewModel();
    this.attachEvents();
    this.updateUI();
  }

  disconnectedCallback() {
    this.cleanupEventListeners();
  }

  getViewModel() {
    return {
      $dropdownInputs: this.querySelectorAll('.drop-down__input'),
      $daySelectInput: this.querySelector('#birthday_dd'),
      $monthSelectInput: this.querySelector('#birthday_mm'),
      $nextButton: this.querySelector(LOYALTY_SELECTORS.NEXT_BUTTON),
      $redirectUrl: this.querySelector(LOYALTY_SELECTORS.REDIRECT_URL),
      $errorMessage: this.querySelector(LOYALTY_SELECTORS.ERROR_MESSAGE),
      $questionIndicator: this.querySelector(LOYALTY_SELECTORS.QUESTION_INDICATOR),
      $dayDropdownMessages: this.querySelector('#birthday_dd')
        .closest('.drop-down')
        .querySelector('.drop-down__messages'),
      $monthDropdownMessages: this.querySelector('#birthday_mm')
        .closest('.drop-down')
        .querySelector('.drop-down__messages'),
    };
  }

  cleanupEventListeners() {
    this._boundHandlers.forEach(({ el, type, fn }) => {
      if (el && typeof el.removeEventListener === 'function') {
        el.removeEventListener(type, fn);
      }
    });
    this._boundHandlers = [];
  }

  addEventHandler(element, type, handler) {
    if (element) {
      element.addEventListener(type, handler);
      this._boundHandlers.push({ el: element, type, fn: handler });
    }
  }

  attachEvents() {
    // Birthday dropdown handlers
    this.viewModel.$dropdownInputs.forEach((dropdown) => {
      const handler = (e) => this.handleDropdownChange(e, dropdown);
      this.addEventHandler(dropdown, 'change', handler);
    });

    // Submit button handler
    this.addEventHandler(this.viewModel.$nextButton, 'click', () => this.handleSubmit());

    this.updateUI();
  }

  handleDropdownChange(e, dropdown) {
    const name = dropdown.getAttribute('name');
    const value = e.target.value;

    if (name === 'day') {
      this.state.selectedDay = value;
    } else if (name === 'month') {
      this.state.selectedMonth = value;
    }

    this.clearError(name);
    this.updateUI();
  }

  async handleSubmit() {
    if (this.state.isSubmitting) return;

    if (this.validateForm()) {
      await this.submitLoyaltyForm();
    }
  }

  validateForm() {
    const errors = {};

    if (!this.state.selectedDay) {
      errors.day = 'Please select your birth day';
    }

    if (!this.state.selectedMonth) {
      errors.month = 'Please select your birth month';
    }

    // Validate date combination
    if (this.state.selectedDay && this.state.selectedMonth) {
      const day = parseInt(this.state.selectedDay);
      const month = parseInt(this.state.selectedMonth);
      const daysInMonth = new Date(2024, month, 0).getDate(); // Use 2024 (leap year) for accurate validation

      if (day > daysInMonth) {
        errors.day = `Invalid date: Month ${month} has only ${daysInMonth} days`;
      }
    }

    this.state.errors = errors;
    this.updateUI();

    return Object.keys(errors).length === 0;
  }

  clearError(field) {
    if (this.state.errors[field]) {
      delete this.state.errors[field];
      this.updateUI();
    }
  }

  updateUI() {
    this.updateButton();
    this.updateErrors();
    this.updateDropdownStates();
  }

  updateButton() {
    if (this.viewModel.$nextButton) {
      const canSubmit = this.canSubmit();
      this.viewModel.$nextButton.disabled = !canSubmit || this.state.isSubmitting;
      this.viewModel.$nextButton.setAttribute('aria-disabled', (!canSubmit || this.state.isSubmitting).toString());

      if (this.state.isSubmitting) {
        this.viewModel.$nextButton.classList.add('is-loading');
      } else {
        this.viewModel.$nextButton.classList.remove('is-loading');
      }
    }
  }

  updateDropdownStates() {
    // Add error state to dropdown containers
    const dayDropdown = this.viewModel.$daySelectInput?.closest('.drop-down');
    const monthDropdown = this.viewModel.$monthSelectInput?.closest('.drop-down');

    if (dayDropdown) {
      if (this.state.errors.day) {
        dayDropdown.classList.add('drop-down__error');
      } else {
        dayDropdown.classList.remove('drop-down__error');
      }
    }

    if (monthDropdown) {
      if (this.state.errors.month) {
        monthDropdown.classList.add('drop-down__error');
      } else {
        monthDropdown.classList.remove('drop-down__error');
      }
    }
  }

  updateErrors() {
    // Update error message in the existing error container or create inline messages
    if (this.viewModel.$errorMessage) {
      const errorMessages = Object.values(this.state.errors);
      if (errorMessages.length > 0) {
        this.viewModel.$errorMessage.textContent = errorMessages[0];
        this.viewModel.$errorMessage.hidden = false;
        this.viewModel.$errorMessage.setAttribute('role', 'alert');
      } else {
        this.viewModel.$errorMessage.hidden = true;
        this.viewModel.$errorMessage.removeAttribute('role');
      }
    }
  }

  canSubmit() {
    return this.state.selectedDay && this.state.selectedMonth;
  }

  async submitLoyaltyForm() {
    this.state.isSubmitting = true;
    this.updateUI();

    const payload = {
      day: this.state.selectedDay,
      month: this.state.selectedMonth,
    };

    try {
      const response = await fetch('/apps/headless-controller/customer-add-birthday', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Server error (${response.status})`;

        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // Use default error message if response isn't JSON
        }

        throw new Error(errorMessage);
      }

      const result = await response.text();
      console.log('Birthday submitted successfully:', result);

      this.handleSubmissionSuccess();
    } catch (error) {
      console.error('Failed to submit birthday:', error);
      this.handleSubmissionError(error);
    } finally {
      this.state.isSubmitting = false;
      this.updateUI();
    }
  }

  handleSubmissionSuccess() {
    const redirectUrl = this.getRedirectUrl();

    // Small delay to show success state before redirect
    setTimeout(() => {
      window.location.href = redirectUrl;
    }, 500);
  }

  handleSubmissionError(error) {
    this.state.errors.submission = error.message || 'Something went wrong. Please try again.';
    this.updateUI();
  }

  getRedirectUrl() {
    const baseUrl = this.viewModel.$redirectUrl?.getAttribute('data-loyalty-redirect-url') || '/';
    const url = new URL(baseUrl, window.location.origin);
    return url.toString();
  }
}

// Define the custom element
customElements.define('loyalty-birthday', LoyaltyBirthday);
